import * as React from 'react';
import { statusText, type CoachStatus } from './status.ts';
import './settings-coach.css';

// The Coach section of Settings, from props only: the switch that lets the
// page connect to the local bridge, where the connection stands, a Connect
// button when it is not up (the page never retries by itself), the steps to
// start the coach, and what stays on this computer. The container in the
// entry point owns the setting and the connection.

export type SaveStatus = 'loading' | 'idle' | 'saving' | 'saved' | 'error';

export interface CoachFormProps {
  /** Whether the page connects; undefined while the setting is read. */
  enabled?: boolean;
  /** Where the connection stands. */
  status: CoachStatus;
  saveStatus: SaveStatus;
  error?: string;
  onChange(enabled: boolean): void;
  /** One dial, now. */
  onConnect(): void;
  /** For a story: a fixed clock for "since hh:mm". */
  time?: (epochMs: number) => string;
}

export const CoachForm: React.FC<CoachFormProps> = ({ enabled, status, saveStatus, error, onChange, onConnect, time }) => {
  const busy = saveStatus === 'loading' || saveStatus === 'saving';
  const canConnect = !!enabled && status.kind === 'disconnected';
  return (
    <section className="panel settings-coach">
      <h2>Coach</h2>
      <div className="settings-coach-form">
        <label className="settings-coach-switch">
          <input type="checkbox" data-track="settings.coach.toggle" data-track-value={enabled ? 'off' : 'on'} checked={!!enabled} disabled={busy || enabled === undefined} onChange={(e) => onChange(e.target.checked)} />
          <span>Connect to the local coach</span>
        </label>
        <p className="settings-coach-help">
          A Claude session on this computer reads your games and drives the board here, through a bridge that runs beside the app. Off, the page never dials; on, it connects once, when the page loads, when the switch is turned on, and when you press Connect.
        </p>
        <div className="settings-coach-actions">
          <span className="settings-coach-state" data-kind={status.kind}>
            {statusText(status, time)}
          </span>
          {canConnect && (
            <button type="button" className="btn" data-track="settings.coach.connect" disabled={busy} onClick={onConnect}>
              Connect
            </button>
          )}
          <span className="settings-coach-status" data-status={saveStatus}>
            {saveStatus === 'loading' && 'Loading…'}
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && 'Saved'}
            {saveStatus === 'error' && (error ?? 'Could not save')}
          </span>
        </div>
        <ol className="settings-coach-steps">
          <li>
            Start the bridge in a terminal: <code>ai/start.sh</code>
          </li>
          <li>Turn the switch on here, or press Connect if it is on already; the line above says Connected.</li>
          <li>
            Start the coach in another terminal: <code>cd ai &amp;&amp; claude</code>
          </li>
        </ol>
        <p className="settings-coach-note">
          Nothing leaves this computer: the page talks to the bridge on 127.0.0.1 and to nothing else, and the bridge talks to the Claude session in that terminal. Safari may refuse the connection from the https site; Chrome and Firefox allow a page to reach localhost.
        </p>
      </div>
    </section>
  );
};
